import React, { useContext, useMemo } from 'react';

import { Flex } from '@gravity-ui/uikit';
import clx from 'classnames';
import { AliceAppActionButton } from 'client/blocks/AliceAppActionButton/AliceAppActionButton';
import { PageLoader } from 'client/blocks/Loader/PageLoader';
import { ModalWithConfirmation } from 'client/blocks/ModalWithConfirmation/ModalWithConfirmation';
import { SmartHomeTestingDate } from 'client/blocks/SmartHomeTestingDate/SmartHomeTestingDate';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { i18nWrapper } from 'client/lib/utils';
import { getDraftStatusHint } from 'client/locale/ru/draftStatus';
import { getSmartHomeTestingChecklistTexts, smartHomeTestingTitleText } from 'client/locale/ru/smartHomeTesting';
import { DraftStatus } from 'client/model/draft';
import { SkillAccess } from 'client/model/settings';
import { Channel, SmartHome } from 'client/model/skill';
import { getCanEditApp } from 'client/utils';
import {
    checkIfCancelled,
    getActionButtons,
    getModerationStatuses,
    getSlaTimings,
    splitModerationStatusesByCurrentStatus,
} from 'client/utils/draft-actions';
import * as keyset from 'i18n/DraftControlPanel';

import styles from './SmartHomeModeration.module.scss';
import { SmartHomeModerationCurrentStatus } from './SmartHomeModerationCurrentStatus';
import { SmartHomeModerationItem } from './SmartHomeModerationItem';
import { SmartHomeValidationStatusesList } from './SmartHomeValidationStatuses';
import { useDevicesTestingStatus } from './useDevicesTestingStatus';
import { useSmartHomeModerationActions } from './useSmartHomeModerationActions';

interface Props {
    aliceApp: SmartHome;
    safeToRequestReview: boolean;
    afterReviewRequest: () => void;
    refreshAliceApp: () => void;
}

const t = i18nWrapper(keyset);

export const SmartHomeModeration: React.FC<Props> = ({
    aliceApp,
    safeToRequestReview,
    afterReviewRequest,
    refreshAliceApp,
}) => {
    const { operations } = useContext(CurrentAliceAppContext)!;
    const { devicesTestingStatusMeta, loadStatusUnhandled, fetchingState } = useDevicesTestingStatus(aliceApp);
    const { actions, error, hasPendingAction, statuses, confirmModalIsOpen, setConfirmModalIsOpen, onConfirm } =
        useSmartHomeModerationActions({ aliceApp, safeToRequestReview, afterReviewRequest, refreshAliceApp });

    const draftStatusHint = useMemo(() => getDraftStatusHint(), []);
    const disabled = !getCanEditApp(aliceApp);
    const isAllowedForDeploy = aliceApp.draft.isAllowedForDeploy;

    if (!operations.length) {
        return <PageLoader />;
    }

    const { status, channel } = aliceApp.draft;
    const moderationStatuses = getModerationStatuses(aliceApp) as Array<DraftStatus>;
    const { cancellationComment, cancelledOnStatus } = checkIfCancelled(operations, channel) ?? {};
    const isCancelled = Boolean(cancelledOnStatus);
    const lastNonCancelledStatus = cancelledOnStatus ?? status;
    const { moderationStatusesBeforeCurrent, moderationStatusesAfterCurrent } = splitModerationStatusesByCurrentStatus(
        moderationStatuses,
        lastNonCancelledStatus,
    );
    const isPrivate = aliceApp.draft.skillAccess === SkillAccess.Private;
    const isMeetingApproval = channel === Channel.SmartHome && status === DraftStatus.MeetingApproval;
    const isTesting =
        channel === Channel.SmartHome &&
        (status === DraftStatus.WaitingForTesting || status === DraftStatus.DevicesTesting);

    return (
        <>
            <div className={styles.smartHomeModeration__container}>
                {moderationStatusesBeforeCurrent.map((draftStatus, i) => (
                    <div className={styles.smartHomeModeration__item} key={i}>
                        {draftStatus === DraftStatus.MeetingApproval && !isPrivate ? (
                            <SmartHomeModerationItem
                                draftStatus={draftStatus}
                                state="completed"
                                position={i + 1}
                                showSuccessText={false}
                            >
                                <SmartHomeTestingDate
                                    aliceApp={aliceApp}
                                    state={fetchingState}
                                    loadStatusUnhandled={loadStatusUnhandled}
                                    devicesTestingStatusMeta={devicesTestingStatusMeta}
                                    disabled
                                />
                            </SmartHomeModerationItem>
                        ) : (
                            <SmartHomeModerationItem draftStatus={draftStatus} state="completed" position={i + 1} />
                        )}
                    </div>
                ))}
                <div className={styles.smartHomeModeration__item}>
                    <SmartHomeModerationItem
                        draftStatus={lastNonCancelledStatus}
                        state={isCancelled ? 'rejected' : 'current'}
                        position={moderationStatusesBeforeCurrent.length + 1}
                        key={moderationStatusesBeforeCurrent.length}
                    >
                        <SmartHomeModerationCurrentStatus
                            aliceApp={aliceApp}
                            status={status}
                            channel={channel}
                            lastNonCancelledStatus={lastNonCancelledStatus}
                            isMeetingApproval={isMeetingApproval}
                            isTesting={isTesting}
                            isCancelled={isCancelled}
                            isPrivate={isPrivate}
                            isAllowedForDeploy={isAllowedForDeploy}
                            cancellationComment={cancellationComment}
                            draftStatusHint={draftStatusHint}
                            fetchingState={fetchingState}
                            loadStatusUnhandled={loadStatusUnhandled}
                            devicesTestingStatusMeta={devicesTestingStatusMeta}
                            disabled={disabled}
                        />
                        <div>
                            <Flex gap={2}>
                                {getActionButtons(aliceApp).map((button) => {
                                    const disabledInvalid = button.settingsShouldBeValid && !isAllowedForDeploy;

                                    return (
                                        <div key={button.action}>
                                            <AliceAppActionButton
                                                action={actions[button.action]}
                                                disabled={hasPendingAction || disabledInvalid || disabled}
                                                view={button.theme}
                                                successLabel={button.action === 'requestDeploy' ? t('published') : ''}
                                            >
                                                {button.getText(isCancelled)}
                                            </AliceAppActionButton>
                                            {disabledInvalid && button.invalidSettingsMessage && (
                                                <div className={clx(styles.label, styles.label_warning)}>
                                                    {button.invalidSettingsMessage}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </Flex>
                            {error && <div className={clx(styles.label, styles.label_error)}>{error}</div>}
                            {statuses && <SmartHomeValidationStatusesList statuses={statuses} />}
                        </div>
                    </SmartHomeModerationItem>
                </div>
                {moderationStatusesAfterCurrent.map((status, i) => {
                    const initialIndex = moderationStatusesBeforeCurrent.length + i + 1;

                    return (
                        <div className={styles.smartHomeModeration__item} key={initialIndex}>
                            <SmartHomeModerationItem
                                draftStatus={status}
                                state="not-started"
                                position={initialIndex + 1}
                                slaTimingsText={getSlaTimings('not-started', channel, status)}
                            />
                        </div>
                    );
                })}
            </div>
            {confirmModalIsOpen && (
                <ModalWithConfirmation
                    confirmButtonLabel={t('continue')}
                    title={t('sh-precheck')}
                    onConfirm={onConfirm}
                    onClose={() => setConfirmModalIsOpen(false)}
                    loading={hasPendingAction}
                >
                    <>
                        <p className={styles.smartHomeConfirmModalChecklist__title}>{smartHomeTestingTitleText}</p>
                        <ul className={styles.smartHomeConfirmModalChecklist__list}>
                            {getSmartHomeTestingChecklistTexts().map((rule, i) => (
                                <li className={styles.smartHomeConfirmModalChecklist__item} key={i}>
                                    {rule}
                                </li>
                            ))}
                        </ul>
                    </>
                </ModalWithConfirmation>
            )}
        </>
    );
};
